import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { translate } from "react-i18next";
import Immutable from "immutable";
import formValueSelector from "redux-form/lib/formValueSelector";
import { createSelector } from "reselect";
import isEqual from "lodash/isEqual";

import SchemaField from "../../../component/SchemaField";
import Address from "../Address";

/**
 * RecipientDetails
 */
class RecipientDetails extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    ctxIdx: PropTypes.number.isRequired,
    isForeignDelivery: PropTypes.bool,
    active: PropTypes.bool,
    className: PropTypes.string,
    addressType: PropTypes.string.isRequired,
    countryCodes: PropTypes.instanceOf(Immutable.List).isRequired,
    schema: PropTypes.instanceOf(Immutable.Map).isRequired
  };

  static contextTypes = {
    namePrefix: PropTypes.string,
    acPrefix: PropTypes.string
  };

  static defaultProps = {
    active: false,
    className: ""
  };

  shouldComponentUpdate(nextProps) {
    // for some reason this does not work in the connect() "areMergedPropsEqual" option
    return !isEqual(this.props, nextProps);
  }

  renderAddressTypeFields() {
    const { schema, addressType } = this.props;
    const prefix = `recipients[${this.props.ctxIdx}]`;
    const addressTypesStrings = this.props.t("addressTypeAdditions", {
      returnObjects: true
    });
    const addressTypeStrings = addressTypesStrings[addressType] || [];
    return (
      <div>
        <SchemaField name={`${prefix}.address.addressType`} schema={schema} />
        {addressTypeStrings.map((addressTypeString, idx) => {
          if (addressType === "funeral center" || addressType === "church") {
            // "addition2" is the date/time this is now displayed as last field on the recipient card
            // https://trello.com/c/kM1glvkU/251-datum-tijd-dienst-naar-laatste-invoer-verplaatsen
            if (idx + 1 === 2) {
              return null;
            }
          }
          return (
            <SchemaField
              key={addressTypeString}
              name={`${prefix}.address.addition${idx + 1}`}
              label={addressTypeStrings[idx]}
              placeholder={addressTypeStrings[idx].toLowerCase()}
              autoComplete="off"
              schema={schema}
            />
          );
        })}
      </div>
    );
  }

  render() {
    const { t, isForeignDelivery, active, countryCodes, schema, className } = this.props;

    const { namePrefix } = this.context;

    return (
      <div className={`${className} Cart__RecipientDetails Cart__Step ${active ? "Cart__Step--active" : ""}`}>
        <div className="Cart__Step__Header">{t("deliveryDetails")}</div>

        {isForeignDelivery && (
          <SchemaField
            name={`${namePrefix}.address.countryCode`}
            autoComplete="off"
            schema={schema}
            disabled={countryCodes.size <= 1}
            options={countryCodes}
          />
        )}

        {this.renderAddressTypeFields()}

        <SchemaField name={`${namePrefix}.address.fullName`} autoComplete="off" schema={schema} />

        <Address
          schema={schema}
          name={`${namePrefix}.address`}
          autoComplete="off"
          hasCountryCode={false}
          helptexts={{
            street: t("street.helptext")
          }}
        />

        {!isForeignDelivery && (
          <SchemaField
            name={`${namePrefix}.address.countryCode`}
            autoComplete="off"
            schema={schema}
            disabled={countryCodes.size <= 1}
            options={countryCodes}
          />
        )}

        <SchemaField
          name={`${namePrefix}.address.phone`}
          autoComplete="off"
          schema={schema}
          tooltip={t("phone.helptext")}
        />
        <SchemaField name={`${namePrefix}.address.sendAnonymously`} schema={schema} />
      </div>
    );
  }
}

const EMPTY_LIST = Immutable.List();

const selectIsForeignDelivery = createSelector(
  [state => state.cart.get("context")],
  context => context.get("isForeignDelivery", false)
);

const selectCountryCodes = createSelector(
  [state => state.cart.get("context")],
  context =>
    context.getIn(
      ["currentItemContext", "deliveryCountryCodes"],
      context.getIn(["itemContexts", 0, "deliveryCountryCodes"], EMPTY_LIST)
    )
);

const schemaSelector = createSelector(
  [
    state => state.cart.get("schema"),
    state => state.cart.get("context"),
    state => selectCountryCodes(state),
    (state, props, addressType) => addressType,
    (state, props) => props.t
  ],
  (schema, context, deliveryCountryCodes, addressType, t) => {
    const path = ["properties", "recipients", "items", "properties", "address", "properties"];

    const fullNamePath = path.concat(["fullName", "title"]);

    const enumValues = deliveryCountryCodes.map(v => v.get("id"));
    const enumTitles = deliveryCountryCodes.map(v => v.get("title"));
    return schema
      .setIn(path.concat(["countryCode", "enum"]), enumValues)
      .setIn(path.concat(["countryCode", "enum_titles"]), enumTitles)
      .setIn(fullNamePath, addressType === "funeral center" ? t("Name of deceased") : schema.getIn(fullNamePath));
  }
);

const valueSelector = formValueSelector("cart");

export default translate()(
  connect((state, props) => {
    const addressType = valueSelector(state, `recipients[${props.ctxIdx}].address.addressType`) || "residence";
    return {
      active: true,
      isForeignDelivery: selectIsForeignDelivery(state),
      schema: schemaSelector(state, props, addressType),
      addressType,
      isCompany: valueSelector(state, "customer.type") === "company",
      countryCodes: selectCountryCodes(state)
    };
  })(RecipientDetails)
);
